import { RecordScreenCard } from "@/lib/Components/Screens/RecordScreen/Cards/RecordScreenCard";
import { Button } from "@/components/catalyst/button";
import { Select } from "@/components/catalyst/select";
import {
  useGqlMutation,
  useSuspenseGqlQuery,
} from "@/lib/GraphQLCodegen/fetcher";
import {
  linkZoomPhoneUserMutation,
  matchZoomPhoneUsersByEmailMutation,
  ZoomPhoneUserItem,
  zoomPhoneStaffQuery,
  zoomPhoneUsersQuery,
} from "@/app/Settings/Admin/ZoomPhone/GraphQL/zoomPhoneUserQueries";
import { toast } from "sonner";

/**
 * Zoom's call logs name the rep by their Zoom user id, so a call is only
 * attributed once that id sits on a staff account.
 */
export function ZoomPhoneUsersTool() {
  const { mutateAsync: matchByEmail, isPending } = useGqlMutation(
    matchZoomPhoneUsersByEmailMutation,
  );

  async function handleMatchByEmail() {
    const { matchZoomPhoneUsersByEmail: result } = await matchByEmail({});

    toast.success(
      `Mapped ${result.linked}, already mapped ${result.unchanged}${
        result.unmatched.length
          ? `, no staff account for ${result.unmatched.join(", ")}`
          : ""
      }`,
    );
  }

  return (
    <RecordScreenCard
      title="Zoom Phone users"
      subtitle="Map each Zoom Phone user to the staff account their calls belong to"
      buttons={
        <Button isLoading={isPending} onClick={handleMatchByEmail}>
          Match by email
        </Button>
      }
    >
      <ZoomPhoneUserTable />
    </RecordScreenCard>
  );
}

function ZoomPhoneUserTable() {
  const { data } = useSuspenseGqlQuery(zoomPhoneUsersQuery, {});

  if (!data.zoomPhoneUsers.length) {
    return (
      <p className="text-sm text-zinc-600">
        No Zoom Phone users. Either Zoom is not configured for this environment,
        or nobody on the account holds a Zoom Phone licence.
      </p>
    );
  }

  return (
    <table className="w-full text-sm">
      <thead>
        <tr className="border-b text-left">
          <th className="pb-2 font-medium">Zoom user</th>
          <th className="pb-2 font-medium">Extension</th>
          <th className="pb-2 font-medium">Staff account</th>
        </tr>
      </thead>
      <tbody>
        {data.zoomPhoneUsers.map((zoomUser) => (
          <tr key={zoomUser.id} className="border-b last:border-0">
            <td className="py-3">
              <p className="font-medium">{zoomUser.name ?? "--"}</p>
              <p className="text-xs text-gray-500">{zoomUser.email}</p>
            </td>
            <td className="py-3">{zoomUser.extension_number ?? "--"}</td>
            <td className="w-96 py-3">
              <ZoomPhoneUserStaffSelect zoomUser={zoomUser} />
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function ZoomPhoneUserStaffSelect({
  zoomUser,
}: {
  zoomUser: ZoomPhoneUserItem;
}) {
  const { data } = useSuspenseGqlQuery(zoomPhoneStaffQuery, { first: 200 });
  const { mutateAsync: link, isPending } = useGqlMutation(
    linkZoomPhoneUserMutation,
  );

  const staff = data.organisationMembers.data.flatMap((member) =>
    member.user ? [member.user] : [],
  );

  return (
    <Select
      disabled={isPending}
      value={zoomUser.user?.id ?? ""}
      onChange={(e) =>
        link({
          zoom_user_id: zoomUser.id,
          extension_id: zoomUser.extension_id,
          user_id: e.target.value || null,
        })
      }
    >
      <option value="">Not mapped</option>
      {staff.map((user) => (
        <option key={user.id} value={user.id}>
          {user.name ?? user.primary_email}
        </option>
      ))}
    </Select>
  );
}
